function calculateBuySell(
  { max = Infinity, min = 50, feesEnabled = true, buyAction = "asks", sellAction = "bids", fees },
  fromPair,
  toPair
) {
  let maxAmount = max;
  const buy = { volume: 0, amount: 0, start: 0, end: 0, count: 0, avg: 0 };
  const sell = { volume: 0, amount: 0, start: 0, end: 0, count: 0, avg: 0 };
  let aksDepth = 0;
  let bidDepth = 0;

  let ask = { ...(fromPair.orderBook?.[buyAction]?.[aksDepth] ?? {}) };
  let bid = { ...(toPair.orderBook?.[sellAction]?.[bidDepth] ?? {}) };

  const buyFee = fees.trading?.from?.buyTakerFee ?? 0;
  const sellFee = fees.trading?.to?.sellTakerFee ?? 0;

  if (buyAction === "bids") {
    if (!isFinite(max)) {
      maxAmount = 500;
    }
    ask.quantity = (maxAmount * 2) / ask.price;
  }
  if (sellAction === "asks") {
    if (!isFinite(max)) {
      maxAmount = 500;
    }
    bid.quantity = (maxAmount * 2) / ask.price;
  }

  let feeAmount = 0;

  if (feesEnabled && !fees.amount) {
    let feeVolume = fees.volume || 0;

    while (ask.price && feeVolume > 0) {
      if (!buy.start) {
        buy.start = ask.price;
      }
      buy.end = ask.price;
      buy.avg = fromPair.orderBook?.[buyAction][Math.floor(aksDepth / 2)].price;

      let buyQuantity = Math.min(feeVolume, ask.quantity);

      ask.quantity -= buyQuantity;
      feeVolume -= buyQuantity;

      buy.volume += buyQuantity;
      buy.amount += ask.price * buyQuantity;

      if (ask.quantity <= 0) {
        aksDepth++;
        buy.count++;
        ask = { ...(fromPair.orderBook?.[buyAction]?.[aksDepth] ?? {}) };
      }
    }

    feeAmount = buy.amount;
  }

  let bestRes = {
    buy: { ...buy },
    sell: { ...sell },
  };
  const minAsk = ask.price;

  while (
    ask.price &&
    bid.price &&
    maxAmount > buy.amount &&
    (minAsk <= bid.price || min > sell.amount)
  ) {
    if (!buy.start) {
      buy.start = ask.price;
    }
    buy.end = ask.price;
    buy.avg = fromPair.orderBook?.[buyAction][Math.floor(aksDepth / 2)].price;

    let buyQuantity = Math.min(ask.quantity, bid.quantity);
    let amount = ask.price * buyQuantity;

    if (buy.amount + amount > maxAmount) {
      buyQuantity = (maxAmount - buy.amount) / ask.price;
    }

    let sellQuantity = buyQuantity * (1 - (fees.depositFeePercent ?? 0));

    sellQuantity = sellQuantity * (1 - (fees.withdrawPercent ?? 0) / 100);
    sellQuantity = sellQuantity * (1 - buyFee);

    ask.quantity -= buyQuantity;
    bid.quantity -= sellQuantity;

    buy.volume += buyQuantity;
    buy.amount += ask.price * buyQuantity;

    if (ask.quantity <= 0) {
      aksDepth++;
      buy.count++;
      ask = { ...(fromPair.orderBook?.[buyAction]?.[aksDepth] ?? {}) };
    }

    if (!sell.start) {
      sell.start = bid.price;
    }
    sell.end = bid.price;
    sell.avg = toPair.orderBook?.[sellAction][Math.floor(bidDepth / 2)].price;

    sell.volume += sellQuantity;

    let sellAmount = bid.price * (sellQuantity * (1 - sellFee));

    if (toPair.to === "BRL") {
      sellAmount = sellAmount / 4.83;
    }

    sell.amount += sellAmount;

    if (bid.quantity <= 0) {
      bidDepth++;
      sell.count++;
      bid = { ...(toPair.orderBook?.[sellAction]?.[bidDepth] ?? {}) };
    }

    const profit = sell.amount - buy.amount;

    if (sell.amount > min && (!bestRes.profit || profit > bestRes.profit + 1)) {
      bestRes = {
        profit,
        buy: { ...buy },
        sell: { ...sell },
      };
    }
  }

  if (!bestRes.profit) {
    bestRes.profit = sell.amount - buy.amount;
  }

  if (!feesEnabled) {
    feeAmount = fees.volume * buy.avg;
  }

  return { ...bestRes, feeAmount };
}

function calculateBuy(amount, fromPair, buyAction) {
  if (buyAction === "bids") {
    const price = fromPair.orderBook?.[buyAction][0].price;
    return {
      amount: amount,
      volume: amount / price,
      start: price,
      end: price,
      avg: price,
      count: 1,
    };
  }

  return fromPair.orderBook?.[buyAction]?.reduce(
    (res, ask, i, arr) => {
      const restAmount = amount - res.amount;
      if (res.amount < amount) {
        if (restAmount / ask.price > ask.quantity) {
          res.amount = res.amount + ask.price * ask.quantity;
          res.volume = res.volume + ask.quantity;
        } else {
          res.amount = res.amount + restAmount;
          res.volume = res.volume + restAmount / ask.price;
        }
        res.count++;
        res.avg = arr[Math.floor(i / 2)].price;
        res.end = ask.price;
        if (!res.start) {
          res.start = ask.price;
        }
      }
      return res;
    },
    { volume: 0, amount: 0, start: 0, end: 0, count: 0, avg: 0 }
  );
}

function calculateSell(toPair, fromAmount, sellAction) {
  if (sellAction === "asks") {
    const price = toPair.orderBook?.[sellAction][0].price;
    return {
      amount: fromAmount * price,
      volume: fromAmount,
      start: price,
      end: price,
      avg: price,
      count: 1,
    };
  }
  return toPair.orderBook?.[sellAction]?.reduce(
    (res, bid, i, arr) => {
      const restVolume = fromAmount - res.volume;
      if (res.volume < fromAmount) {
        if (restVolume > bid.quantity) {
          res.amount = res.amount + bid.price * bid.quantity;
          res.volume = res.volume + bid.quantity;
        } else {
          res.amount = res.amount + restVolume * bid.price;
          res.volume = res.volume + restVolume;
        }
        res.count++;
        res.avg = arr[Math.floor(i / 2)].price;
        res.end = bid.price;
        if (!res.start) {
          res.start = bid.price;
        }
      }
      return res;
    },
    { amount: 0, volume: 0, start: 0, end: 0, count: 0, avg: 0 }
  );
}

module.exports = {
  calculateBuy,
  calculateSell,
  calculateBuySell,
};
